import React, { useState, useEffect, useCallback, useRef } from "react";
import { useHandoffAuth } from "./hooks/useHandoffAuth";
import useTranslator from "../../hooks/useTranslator";
import CaptureScreen from "./components/CaptureScreen";
import ReviewScreen from "./components/ReviewScreen";
import { uploadDocument, submitSession } from "./services/mobileCaptureApi";

type PhoneStep =
  | "loading"
  | "guide"
  | "capture_front"
  | "review_front"
  | "capture_back"
  | "review_back"
  | "uploading"
  | "done"
  | "error";

interface MobileCaptureProps {
  sessionToken: string;
}

const MobileCapture: React.FC<MobileCaptureProps> = ({ sessionToken }) => {
  const { handoffToken, sessionInfo, isLoading, error, open } =
    useHandoffAuth(sessionToken);
  const { _t } = useTranslator();

  const [step, setStep] = useState<PhoneStep>("loading");
  const [frontImage, setFrontImage] = useState<File | Blob | null>(null);
  const [backImage, setBackImage] = useState<File | Blob | null>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const isSubmitting = useRef(false);

  useEffect(() => {
    open();
  }, [open]);

  useEffect(() => {
    if (handoffToken && sessionInfo) {
      if (
        sessionInfo.status === "PHONE_OPENED" ||
        sessionInfo.status === "PENDING"
      ) {
        setStep("guide");
      }
    }
  }, [handoffToken, sessionInfo]);

  useEffect(() => {
    if (error) {
      setStep("error");
    }
  }, [error]);

  const handleCaptureFront = useCallback((imageBlob: File | Blob) => {
    setFrontImage(imageBlob);
    setStep("review_front");
  }, []);

  const handleCaptureBack = useCallback((imageBlob: File | Blob) => {
    setBackImage(imageBlob);
    setStep("review_back");
  }, []);



  const handleConfirmFront = useCallback(async () => {
    if (!frontImage || isSubmitting.current) return;
    isSubmitting.current = true;
    setStep("uploading");
    try {
      await uploadDocument(sessionToken, frontImage as File, "front");
      if (sessionInfo?.requires_back) {
        setStep("capture_back");
        setUploadError(null);
      } else {
        await submitSession(sessionToken);
        setStep("done");
      }
    } catch (err: any) {
      setUploadError(err?.message || _t("mobileCaptureUploadError"));
      setStep("review_front");
    } finally {
      isSubmitting.current = false;
    }
  }, [frontImage, sessionToken, sessionInfo, _t]);

  const handleConfirmBack = useCallback(async () => {
    if (!backImage || isSubmitting.current) return;
    isSubmitting.current = true;
    setStep("uploading");
    try {
      await uploadDocument(sessionToken, backImage as File, "back");
      await submitSession(sessionToken);
      setStep("done");
    } catch (err: any) {
      setUploadError(err?.message || _t("mobileCaptureUploadError"));
      setStep("review_back");
    } finally {
      isSubmitting.current = false;
    }
  }, [backImage, sessionToken, _t]);

  const handleRetakeFront = useCallback(() => {
    setFrontImage(null);
    setUploadError(null);
    setStep("capture_front");
  }, []);

  const handleRetakeBack = useCallback(() => {
    setBackImage(null);
    setUploadError(null);
    setStep("capture_back");
  }, []);

  if (isLoading) {
    return (
      <div className="mobile-capture-screen mobile-capture-loading">
        <div className="mobile-capture-spinner" />
        <p>{_t("mobileCaptureOpening")}</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="mobile-capture-screen mobile-capture-error">
        <div className="mobile-capture-error-icon">!</div>
        <h3>{_t("mobileCaptureErrorTitle")}</h3>
        <p>{error}</p>
      </div>
    );
  }

  if (step === "guide" && !sessionInfo?.requires_back) {
    return (
      <div className="mobile-capture-screen">
        <div className="mobile-capture-guide">
          <div className="mobile-capture-guide-content">
            <div className="mobile-capture-brand-mark" aria-hidden="true">
              <img src="https://sdk.onfido.com/assets/icons/identity-icon.svg" alt="" />
            </div>
            <h3>{_t("mobileCaptureGuideTitle", "Front of ID")}</h3>
            <p>{_t("mobileCaptureGuideFrontOnly", "We need a clear photo of the front of your document.")}</p>
            <div className="mobile-capture-guide-note">
              <span aria-hidden="true">✓</span>
              {_t("Use the original document in good light")}
            </div>
          </div>
          <div className="mobile-capture-guide-footer">
            <button
              className="mobile-capture-btn mobile-capture-btn-primary"
              onClick={() => setStep("capture_front")}
            >
              {_t("mobileCaptureStart", "Start")}
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (step === "guide") {
    return (
      <div className="mobile-capture-screen">
        <div className="mobile-capture-guide">
          <div className="mobile-capture-guide-content">
            <div className="mobile-capture-brand-mark" aria-hidden="true">
              <img src="https://sdk.onfido.com/assets/icons/identity-icon.svg" alt="" />
            </div>
            <h3>{_t("mobileCaptureGuideTitle", "Front and back of ID")}</h3>
            <p>
              {frontImage
                ? _t("mobileCaptureGuideBack", "We need a clear photo of the back of your document.")
                : _t("mobileCaptureGuideBoth", "We need a clear photo of both sides of your document.")}
            </p>
            <div
              className="mobile-capture-step-preview"
              aria-label={_t("Two photos required")}
            >
              <span className={frontImage ? 'completed' : 'active'}>
                <strong>1</strong>
                {_t("Front")}
              </span>
              <span className={frontImage ? 'active' : ''}>
                <strong>2</strong>
                {_t("Back")}
              </span>
            </div>
          </div>
          <div className="mobile-capture-guide-footer">
            <button
              className="mobile-capture-btn mobile-capture-btn-primary"
              onClick={() =>
                setStep(frontImage ? "capture_back" : "capture_front")
              }
            >
              {frontImage
                ? _t("mobileCaptureStartBack", "Scan back of ID")
                : _t("mobileCaptureStartFront", "Scan front of ID")}
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (step === "capture_front") {
    return (
      <CaptureScreen
        side="front"
        onCapture={handleCaptureFront}
        onCancel={() => setStep("guide")}
      />
    );
  }

  if (step === "capture_back") {
    return (
      <CaptureScreen
        side="back"
        onCapture={handleCaptureBack}
        onCancel={() => setStep("guide")}
      />
    );
  }

  if (step === "review_front" && frontImage) {
    return (
      <ReviewScreen
        image={frontImage}
        side="front"
        error={uploadError}
        onConfirm={handleConfirmFront}
        onRetake={handleRetakeFront}
      />
    );
  }

  if (step === "review_back" && backImage) {
    return (
      <ReviewScreen
        image={backImage}
        side="back"
        error={uploadError}
        onConfirm={handleConfirmBack}
        onRetake={handleRetakeBack}
      />
    );
  }

  if (step === "uploading") {
    return (
      <div className="mobile-capture-screen mobile-capture-loading">
        <div className="mobile-capture-spinner" />
        <p>{_t("mobileCaptureUploading")}</p>
      </div>
    );
  }

  if (step === "done") {
    return (
      <div className="mobile-capture-screen mobile-capture-done">
        <div className="mobile-capture-done-icon">✓</div>
        <h3>{_t("mobileCaptureDoneTitle")}</h3>
        <p>{_t("mobileCaptureDoneDesc")}</p>
      </div>
    );
  }

  return null;
};

export default MobileCapture;
